import * as React from 'react';

import { cn } from '@codes/ui-kit/utils';
import * as TabsPrimitive from '@radix-ui/react-tabs';

const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef<
    React.ElementRef<typeof TabsPrimitive.List>,
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => {
    return (
        <div>
            <TabsPrimitive.List
                className={cn(
                    'md:pt-[14px] w-full h-auto inline-flex items-center justify-center max-md:justify-start text-foreground overflow-auto overflow-y-hidden scrollbar-hide',
                    className
                )}
                {...props}
            />
        </div>
    );
});
TabsList.displayName = TabsPrimitive.List.displayName;

interface TabsTriggerProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> {
    variant?: 'default' | 'underline';
}

const TabsTrigger = React.forwardRef<React.ElementRef<typeof TabsPrimitive.Trigger>, TabsTriggerProps>(
    ({ className, variant = 'default', ...props }, ref) => (
        <TabsPrimitive.Trigger
            ref={ref}
            className={cn(
                'relative inline-flex items-center justify-center whitespace-nowrap px-4 py-1 text-base max-xs:text-sm text-tab-disabled transition-all',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                'disabled:pointer-events-none disabled:opacity-50',
                variant === 'default' && [
                    'border-b border-border h-11 md:w-[180px]',
                    'data-[state=active]:text-tab-active data-[state=active]:border-b-2 data-[state=active]:!border-point',
                ],
                variant === 'underline' && [
                    'relative px-3 py-1.5 text-sm text-text-400 font-medium',
                    'after:content-[""] after:absolute after:-bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-4 after:h-[2px] after:rounded-full after:bg-transparent',
                    'data-[state=active]:text-text data-[state=active]:after:bg-point',
                ],
                className
            )}
            {...props}
        />
    )
);
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
    React.ElementRef<typeof TabsPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
    <TabsPrimitive.Content
        ref={ref}
        className={cn(
            'ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            className
        )}
        {...props}
    />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
